/* Scroll reveal — entrance animations for the Professionals page (WEB-6278).
 *
 * Parity target: sexyhair.com/professionals/. The legacy theme drives its
 * entrances through `data-ca-options` attributes (a GSAP "custom animations"
 * plugin). That page carries three distinct configs, but two of them differ
 * only in which elements they target — their durations, offsets and stagger
 * are identical — so they collapse into a single variant here. That leaves
 * TWO variants, reproduced with CSS transitions and one IntersectionObserver
 * (an API that fires a callback when an element scrolls into view — no scroll
 * polling):
 *
 *   lift  — opacity 0 → 1, translateY 150px → 0, 1600ms, 250ms stagger.
 *           This is the collapsed pair: legacy targets "all-childs" on the
 *           intro/class/artist bands and ".wpb_column" on the band headings +
 *           educator rows, at the same values.
 *   drop  — opacity 0 → 1, translateY -30px → 0, 1200ms, 120ms stagger,
 *           500ms lead-in, reversed order. Legacy targets "h3,h6" — the
 *           artist name + role plate.
 *
 * Legacy's easing is GSAP `power4.out`; `cubic-bezier(0.22, 1, 0.36, 1)` is
 * the CSS equivalent (see the --sh-ease default in sexyhair-utilities.css).
 *
 * Above-the-fold content DOES animate on load. That is deliberate parity:
 * legacy's trigger is "inview", which is already satisfied at load for the
 * hero, so the hero animates on arrival. (The blog's sexyhair-article-animate.js
 * makes the opposite choice for its own surface — see the note in the PR.)
 *
 * Fail-open by design: the hidden initial state is gated on
 * html.sh-reveal-ready, which is stamped HERE. If this file never loads, never
 * parses, or bails early, nothing is ever hidden. Reduced motion is handled in
 * CSS, not here, so it cannot be defeated by a JS failure either.
 */
(() => {
  const READY = 'sh-reveal-ready';
  const html = document.documentElement;
  if (html.classList.contains(READY)) return;

  const groups = document.querySelectorAll('[data-sh-reveal]');
  if (!groups.length || !('IntersectionObserver' in window)) return;

  html.classList.add(READY);

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('sh-revealed');
        io.unobserve(entry.target);
      }
    },
    // Matches legacy's "inview": fire as soon as any part of the band enters.
    { threshold: 0, rootMargin: '0px 0px -5% 0px' }
  );

  groups.forEach((group) => {
    // "self" animates the group as one unit; "children" (default) staggers its
    // direct children.
    const targets = group.dataset.shReveal === 'self' ? [group] : Array.from(group.children);
    if (!targets.length) return;

    const backward = group.dataset.shRevealOrder === 'backward';
    targets.forEach((el, i) => {
      el.classList.add('sh-reveal-item');
      // --sh-i is the stagger slot. CSS turns it into a transition-delay, so
      // reversing the order here is all "direction: backward" needs.
      el.style.setProperty('--sh-i', backward ? targets.length - 1 - i : i);
    });

    io.observe(group);
  });
})();
